<template>

    <div>
        <div id="campaigns" class="flex">
            <h1>{{ submitName }}</h1>

            <nav>
                <button @click="schedule.navigation.goBack" type="button" class="alt">Go Back</button>
            </nav>
        </div>

        <div class="top-help-block">
            <div>
                <span class="text-brand-600">*</span> denotes required field
            </div>
        </div>

        <form action="#" method="post" enctype="multipart/form-data" v-if="schedule.isLoaded()" @submit.prevent="schedule.submitSchedule()">

            <section class="admin-form panel-form">
                <aside>
                    <h2 class="font-medium text-lg">Report</h2>
                    <p>
                        Select the report you would like to run on a schedule.
                    </p>
                </aside>
                <div class="form-group">

                    <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': schedule.fieldHasErrors('report_id') }">
                        <label for="report_id">Report<span class="req-asterisk">*</span></label>
                        <div class="mt-2">
                            <select name="report_id" id="report_id" required aria-required="true" autocomplete="off" v-model="schedule.data.report_id" @change="schedule.selectReport()">
                                <option :value="null">Select Report</option>
                                <option :value="report.id" v-for="report in schedule.reports">{{ report.name }}</option>
                            </select>
                        </div>
                        <p class="input-error-message text-sm" v-if="schedule.fieldHasErrors('report_id')">
                            {{  schedule.getFieldError('report_id') }}
                        </p>
                    </div>
                </div>
            </section>

            <section class="admin-form panel-form">
                <aside>
                    <h2 class="font-medium text-lg">Report Parameters</h2>
                    <p>
                        Select the report parameters that will be run against the report (any date and time fields will be automatically handled by the report schedule frequency).
                    </p>
                </aside>
                <div class="form-group" v-if="!schedule.data.report_id">

                    <div class="col-span-6 p-10 mt-4" >
                        <div class="text-slate-300 my-6">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>

                        <h2 class="text-slate-600 font-medium text-2xl text-center">Select Report</h2>

                        <p class="text-center mt-4 text-sm text-slate-700">
                            You must select a report first before being able to edit parameters.
                        </p>
                    </div>

                </div>
                <div class="form-group" v-else-if="schedule.selected && schedule.isLoaded()">

                    <div class="col-span-6 xl:col-span-4" v-for="field in schedule.selected.fields">
                        <label :for="`field[${field.alias}]`">{{  field.label }}</label>
                        <div class="mt-2">
                            <select v-if="field.type === 'select'" :name="`field[${field.alias}]`" :id="`field[${field.alias}]`" autocomplete="off" v-model="schedule.data.parameters[field.alias]">
                                <option :value="null">Select {{ field.alias }}</option>
                                <option :value="option[field.model_select_value]" v-for="option in field.options">
                                    {{ option[field.model_select_name] }}
                                </option>
                            </select>

                            <div v-else-if="field.type === 'date' || field.type === 'datetime' || field.type === 'time'">
                                <div class="form-input-style-disabled">{{ field.alias }}</div>

                                <small>This field will be automatically handled by the report schedule frequency.</small>
                            </div>

                            <input v-else :type="field.type" :name="`field[${field.alias}]`" :id="`field[${field.alias}]`" autocomplete="off" v-model="schedule.data.parameters[field.alias]">

                        </div>
                        <p class="input-error-message text-sm" v-if="schedule.fieldHasErrors('field')">
                            {{  schedule.getFieldError('field') }}
                        </p>
                    </div>

                    <div class="col-span-6 xl:col-span-4 ">
                        <label :for="`field[export_driver]`">Export Type<span class="req-asterisk">*</span></label>
                        <div class="mt-2">
                            <select :name="`field[export_driver]`" :id="`field[export_driver]`" required aria-required="true" autocomplete="off" v-model="schedule.data.parameters['export_driver']">
                                <option :value="null">Select Field Type</option>
                                <option :value="item.value" v-for="item in schedule.selected.queued_export_drivers">{{ item.name }}</option>
                            </select>
                        </div>
                        <p class="input-error-message text-sm" v-if="schedule.fieldHasErrors('field.export_driver')">
                            {{  schedule.getFieldError('field.export_driver') }}
                        </p>
                    </div>

                </div>
                <div class="form-group" v-else>
                    <div class="w-full text-center mx-auto col-span-4">
                        <Loading message="Loading Report"></Loading>
                    </div>
                </div>
            </section>

            <section class="admin-form panel-form">
                <aside>
                    <h2 class="font-medium text-lg">Schedule</h2>
                    <p>
                        Setup the schedule for this report to run against, please note that reports are run at designated
                        periods depending on the frequency selected (e.g. Daily at 00:00:00Z, Weekly Monday at 00:00:00Z,
                        Monthly 01st of Month at 00:00:00Z) subtracting the relevant date (e.g. Daily runs between
                        00:00:00Z to 23:59:59Z for the previous day).
                    </p>
                </aside>
                <div class="form-group">

                    <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': schedule.fieldHasErrors('frequency') }">
                        <label for="frequency">Frequency<span class="req-asterisk">*</span></label>
                        <div class="mt-2">
                            <select name="frequency" id="frequency" required aria-required="true" autocomplete="off" v-model="schedule.data.frequency">
                                <option :value="frequency.id" v-for="frequency in schedule.frequencies">{{ frequency.name }}</option>
                            </select>
                        </div>
                        <p class="input-error-message text-sm" v-if="schedule.fieldHasErrors('frequency')">
                            {{  schedule.getFieldError('frequency') }}
                        </p>
                    </div>

                    <div class="col-span-6 xl:col-span-4" :class="{ 'form-control-error': schedule.fieldHasErrors('recipients') }">
                        <label for="recipients">Recipients</label>
                        <div class="mt-2">
                            <input type="text" name="recipients" id="recipients" autocomplete="off" v-model="schedule.data.recipients">

                            <small>
                                A list of recipients that report results will be emailed to
                                (comma seperated <span class="font-medium">e.g. j.doe@example.org, finance@example.org, tech@example.org</span>)
                            </small>
                        </div>
                        <p class="input-error-message text-sm" v-if="schedule.fieldHasErrors('recipients')">
                            {{  schedule.getFieldError('recipients') }}
                        </p>
                    </div>

                </div>
            </section>

            <div class="flex px-1">
                <div class="flex-1">
                    <p class="success-message" v-if="schedule.successful()">
                        {{ schedule.getSuccessMessage() }}
                    </p>

                    <p class="error-message" v-if="schedule.hasErrorOccurred()">
                        {{ schedule.getErrorMessage() }}
                    </p>
                </div>

                <div>
                    <button type="submit" :disabled="schedule.isSubmitting()" :aria-disabled="schedule.isSubmitting()">
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-show="schedule.isSubmitting()">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ submitName }}
                    </button>
                </div>
            </div>

        </form>
        <Loading v-else></Loading>
    </div>

</template>

<script>
import {defineComponent} from "vue";
import {administration} from "../../../../app/Support/Props";
import {ReportSchedule} from "../../../../app/Report/ReportSchedule";
import Loading from "../../../Support/Loading";
import DateTimePicker from "@/components/Support/DateTimePicker.vue";

export default defineComponent({
    props: administration,
    components: {DateTimePicker, Loading},
    data() {
        return {
            schedule: new ReportSchedule(this.app, this.$route.params)
        }
    },
    computed: {
        submitName() {
            if (this.schedule.data.uuid) {
                return 'Update Schedule';
            }

            return 'Create Schedule';
        }
    },
    mounted() {
        this.schedule.mount();
    }
})
</script>
